/** 前台检索高亮（与后端 _manual_text_highlight 规则对齐，ES 无 highlight 时兜底） */

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function minTermLen(term: string): number {
  return /[\u4e00-\u9fff]/.test(term) ? 1 : 2
}

/** 从 query 拆出应高亮的词（整句、去空格、空格分词、英数专名、中文片段） */
export function queryHighlightTerms(query: string): string[] {
  const raw = query.trim()
  if (!raw) return []
  const terms: string[] = []
  const seen = new Set<string>()

  const add = (t: string) => {
    const x = t.trim()
    if (!x || seen.has(x.toLowerCase()) || x.length < minTermLen(x)) return
    seen.add(x.toLowerCase())
    terms.push(x)
  }

  add(raw)
  add(raw.replace(/\s+/g, ''))
  for (const part of raw.split(/\s+/)) add(part)
  const compact = raw.replace(/\s+/g, '')
  if (/^[A-Za-z0-9][A-Za-z0-9\-_./\\]*$/.test(compact)) add(compact)
  for (const m of raw.matchAll(/[A-Za-z]\d+/gi)) add(m[0].toUpperCase())
  for (const m of raw.matchAll(/[A-Za-z0-9][A-Za-z0-9\-_./\\]{1,31}/g)) add(m[0])
  for (const m of raw.matchAll(/[\u4e00-\u9fff]+/g)) {
    const seg = m[0]
    add(seg)
    if (seg.length > 2) {
      for (let i = 0; i < seg.length - 1; i++) add(seg.slice(i, i + 2))
    }
  }
  return terms.sort((a, b) => b.length - a.length)
}

function mergeSpans(spans: Array<{ start: number; end: number }>) {
  if (!spans.length) return spans
  const sorted = [...spans].sort((a, b) => a.start - b.start || b.end - a.end)
  const out = [sorted[0]]
  for (const s of sorted.slice(1)) {
    const last = out[out.length - 1]
    if (s.start <= last.end) {
      last.end = Math.max(last.end, s.end)
    } else {
      out.push({ ...s })
    }
  }
  return out
}

/** 返回带 <em class="search-hl"> 的 HTML；无匹配则 null */
export function highlightSearchQuery(text: string, query: string): string | null {
  const body = text.trim()
  const q = query.trim()
  if (!body || !q) return null

  const terms = queryHighlightTerms(q)
  if (!terms.length) return null

  const lowBody = body.toLowerCase()
  const spans: Array<{ start: number; end: number }> = []

  for (const cand of terms) {
    const lowCand = cand.toLowerCase()
    let from = 0
    while (from < lowBody.length) {
      const pos = lowBody.indexOf(lowCand, from)
      if (pos < 0) break
      spans.push({ start: pos, end: pos + cand.length })
      from = pos + Math.max(1, cand.length)
    }
  }
  const merged = mergeSpans(spans)
  if (!merged.length) return null

  let html = ''
  let cursor = 0
  for (const { start, end } of merged) {
    if (start < cursor) continue
    html += escapeHtml(body.slice(cursor, start))
    html += `<em class="search-hl">${escapeHtml(body.slice(start, end))}</em>`
    cursor = end
  }
  html += escapeHtml(body.slice(cursor))
  return html
}

export function hasSearchHighlight(html: string | undefined | null): boolean {
  return Boolean(html && /<em[\s>]/i.test(html))
}

/** 优先本地高亮；ES 高亮仅作兜底 */
export function resolveSearchHighlight(
  text: string,
  query: string,
  serverHtml?: string | null,
): string | null {
  const local = highlightSearchQuery(text, query)
  if (local) return local
  if (hasSearchHighlight(serverHtml)) return serverHtml!
  return null
}
